<script setup lang="ts">
import { ref } from 'vue'
import type { FlyerData } from '@/types'
import { formatPhone } from '@/utils/phone'

export type { FlyerData }

const props = defineProps<{
  modelValue: FlyerData
  photoUrl: string | null
  agencyLogo: string | null
  agencyName: string | null
  secondaryAgencyLogo: string | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: FlyerData]
  'change:agencyLogo': [dataUrl: string]
  'change:secondaryAgencyLogo': [dataUrl: string]
}>()

const photoError = ref(false)
const hostLogoInput = ref<HTMLInputElement | null>(null)
const requestingLogoInput = ref<HTMLInputElement | null>(null)

function set(field: keyof FlyerData, value: string) {
  emit('update:modelValue', { ...props.modelValue, [field]: value })
}

function onLogoFile(event: Event, which: 'host' | 'requesting') {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (!file) return
  const reader = new FileReader()
  reader.onload = (e) => {
    const dataUrl = e.target?.result as string
    if (!dataUrl) return
    if (which === 'host') emit('change:agencyLogo', dataUrl)
    else emit('change:secondaryAgencyLogo', dataUrl)
  }
  reader.readAsDataURL(file)
  ;(event.target as HTMLInputElement).value = ''
}
</script>

<template>
  <!--
    Fixed-width white card simulating letter-size paper.
    All text fields are inline-editable: inputs are styled transparent by default,
    showing a subtle highlight on hover and an orange underline on focus.
  -->
  <div
    class="bg-white shadow-md mx-auto select-none"
    style="width: 680px; min-width: 680px; font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #111;"
  >

    <!-- Logo row -->
    <div class="flex items-center justify-between p-3 pb-0 mb-2.5" style="min-height: 64px;">

      <!-- Host agency logo (left) -->
      <div class="relative group" style="width:180px;height:52px;">
        <input ref="hostLogoInput" type="file" accept="image/*" class="hidden" @change="onLogoFile($event, 'host')" />
        <img v-if="agencyLogo" :src="agencyLogo" alt="Host agency logo" class="max-h-full max-w-full object-contain" />
        <div
          v-else
          class="flex flex-col items-center justify-center w-full h-full bg-slate-100 border border-slate-200 text-slate-400 text-xs gap-0.5"
        >
          <span style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Host Agency</span>
          <span style="font-size:10px;">No logo set</span>
        </div>
        <!-- Change overlay -->
        <button
          type="button"
          class="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
          style="border-radius:2px;"
          @click="hostLogoInput?.click()"
        >
          <span class="bg-white/90 text-slate-700 font-semibold rounded px-2 py-0.5" style="font-size:10px;text-transform:uppercase;letter-spacing:0.5px;">Change</span>
        </button>
      </div>

      <!-- Requesting agency logo (right) -->
      <div class="relative group" style="width:180px;height:52px;">
        <input ref="requestingLogoInput" type="file" accept="image/*" class="hidden" @change="onLogoFile($event, 'requesting')" />
        <img v-if="secondaryAgencyLogo" :src="secondaryAgencyLogo" alt="Requesting agency logo" class="max-h-full max-w-full object-contain" style="margin-left:auto;display:block;" />
        <div
          v-else
          class="flex flex-col items-center justify-center w-full h-full bg-slate-100 border border-slate-200 text-slate-400 text-xs gap-0.5"
        >
          <span style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Requesting Agency</span>
          <span style="font-size:10px;">No logo set</span>
        </div>
        <!-- Change overlay -->
        <button
          type="button"
          class="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
          style="border-radius:2px;"
          @click="requestingLogoInput?.click()"
        >
          <span class="bg-white/90 text-slate-700 font-semibold rounded px-2 py-0.5" style="font-size:10px;text-transform:uppercase;letter-spacing:0.5px;">Change</span>
        </button>
      </div>

    </div>

    <!-- MISSING PERSON header -->
    <div class="mx-3 mb-2.5 text-center text-white" style="background-color: #ea580c; padding: 10px 8px;">
      <div class="font-bold tracking-widest uppercase" style="font-size: 22px;">MISSING PERSON – AT RISK</div>
      <div class="mt-1" style="font-size: 13px;">
        At Risk –&nbsp;
        <input
          :value="modelValue.risk_category_label"
          placeholder="Risk category…"
          class="bg-transparent text-white font-semibold text-center border-b border-transparent hover:border-white/60 focus:border-white focus:outline-none placeholder-white/50"
          style="width: 200px; font-size: 13px; font-family: inherit;"
          @input="set('risk_category_label', ($event.target as HTMLInputElement).value)"
        />
      </div>
    </div>

    <!-- Subject photo -->
    <div class="text-center mb-2.5">
      <img
        v-if="photoUrl && !photoError"
        :src="photoUrl"
        alt="Subject photo"
        class="inline-block object-cover"
        style="max-height: 240px; max-width: 224px;"
        @error="photoError = true"
      />
      <div
        v-else
        class="inline-flex flex-col items-center justify-center bg-slate-100 border-2 border-dashed border-slate-300"
        style="width: 224px; height: 240px; gap: 8px;"
      >
        <img src="/assets/subject-placeholder.svg" alt="No photo" class="w-20 h-20 opacity-30" />
        <span class="text-slate-400 font-semibold uppercase tracking-wide" style="font-size: 11px;">Photo Pending</span>
        <span class="text-slate-300" style="font-size: 10px;">Add via Subject Tab</span>
      </div>
    </div>

    <!-- Subject name (editable) -->
    <div class="text-center mb-2.5 px-3" style="font-size: 22px; font-weight: bold; letter-spacing: 0.5px;">
      <input
        :value="modelValue.subject_name"
        class="text-center font-bold bg-transparent border-b border-transparent hover:border-slate-400 focus:border-orange-500 focus:outline-none w-full"
        style="font-size: 22px; font-family: inherit; letter-spacing: 0.5px;"
        @input="set('subject_name', ($event.target as HTMLInputElement).value)"
      />
    </div>

    <!-- Data table -->
    <table class="mb-2.5" style="border-collapse: collapse; width: calc(100% - 24px); margin: 0 12px 10px; font-size: 11px;">
      <tbody>
      <tr>
        <th class="text-white text-left uppercase tracking-wide" style="background:#374151; padding:4px 8px; width:16%; font-size:10px; white-space:nowrap;">Missing Since</th>
        <td class="border-b border-slate-200" style="padding:2px 4px; width:34%;">
          <input :value="modelValue.missing_since" placeholder="—" class="w-full bg-transparent border-b border-transparent hover:border-slate-400 focus:border-orange-500 focus:outline-none" style="font-size:11px;font-family:inherit;" @input="set('missing_since', ($event.target as HTMLInputElement).value)" />
        </td>
        <th class="text-white text-left uppercase tracking-wide" style="background:#374151; padding:4px 8px; width:16%; font-size:10px; white-space:nowrap;">Last Seen</th>
        <td class="border-b border-slate-200" style="padding:2px 4px; width:34%;">
          <input :value="modelValue.last_seen" placeholder="—" class="w-full bg-transparent border-b border-transparent hover:border-slate-400 focus:border-orange-500 focus:outline-none" style="font-size:11px;font-family:inherit;" @input="set('last_seen', ($event.target as HTMLInputElement).value)" />
        </td>
      </tr>
      <tr style="background:#f9fafb;">
        <th class="text-white text-left uppercase tracking-wide" style="background:#374151; padding:4px 8px; font-size:10px;">Age</th>
        <td class="border-b border-slate-200" style="padding:2px 4px;">
          <input :value="modelValue.age" placeholder="—" class="w-full bg-transparent border-b border-transparent hover:border-slate-400 focus:border-orange-500 focus:outline-none" style="font-size:11px;font-family:inherit;" @input="set('age', ($event.target as HTMLInputElement).value)" />
        </td>
        <th class="text-white text-left uppercase tracking-wide" style="background:#374151; padding:4px 8px; font-size:10px;">Sex</th>
        <td class="border-b border-slate-200" style="padding:2px 4px;">
          <input :value="modelValue.sex" placeholder="—" class="w-full bg-transparent border-b border-transparent hover:border-slate-400 focus:border-orange-500 focus:outline-none" style="font-size:11px;font-family:inherit;" @input="set('sex', ($event.target as HTMLInputElement).value)" />
        </td>
      </tr>
      <tr>
        <th class="text-white text-left uppercase tracking-wide" style="background:#374151; padding:4px 8px; font-size:10px;">Height</th>
        <td class="border-b border-slate-200" style="padding:2px 4px;">
          <input :value="modelValue.height" placeholder="—" class="w-full bg-transparent border-b border-transparent hover:border-slate-400 focus:border-orange-500 focus:outline-none" style="font-size:11px;font-family:inherit;" @input="set('height', ($event.target as HTMLInputElement).value)" />
        </td>
        <th class="text-white text-left uppercase tracking-wide" style="background:#374151; padding:4px 8px; font-size:10px;">Weight</th>
        <td class="border-b border-slate-200" style="padding:2px 4px;">
          <input :value="modelValue.weight" placeholder="—" class="w-full bg-transparent border-b border-transparent hover:border-slate-400 focus:border-orange-500 focus:outline-none" style="font-size:11px;font-family:inherit;" @input="set('weight', ($event.target as HTMLInputElement).value)" />
        </td>
      </tr>
      <tr style="background:#f9fafb;">
        <th class="text-white text-left uppercase tracking-wide" style="background:#374151; padding:4px 8px; font-size:10px;">Race</th>
        <td class="border-b border-slate-200" style="padding:2px 4px;">
          <input :value="modelValue.race" placeholder="—" class="w-full bg-transparent border-b border-transparent hover:border-slate-400 focus:border-orange-500 focus:outline-none" style="font-size:11px;font-family:inherit;" @input="set('race', ($event.target as HTMLInputElement).value)" />
        </td>
        <th class="text-white text-left uppercase tracking-wide" style="background:#374151; padding:4px 8px; font-size:10px;">Complexion</th>
        <td class="border-b border-slate-200" style="padding:2px 4px;">
          <input :value="modelValue.complexion" placeholder="—" class="w-full bg-transparent border-b border-transparent hover:border-slate-400 focus:border-orange-500 focus:outline-none" style="font-size:11px;font-family:inherit;" @input="set('complexion', ($event.target as HTMLInputElement).value)" />
        </td>
      </tr>
      <tr>
        <th class="text-white text-left uppercase tracking-wide" style="background:#374151; padding:4px 8px; font-size:10px;">Hair</th>
        <td class="border-b border-slate-200" style="padding:2px 4px;">
          <input :value="modelValue.hair" placeholder="—" class="w-full bg-transparent border-b border-transparent hover:border-slate-400 focus:border-orange-500 focus:outline-none" style="font-size:11px;font-family:inherit;" @input="set('hair', ($event.target as HTMLInputElement).value)" />
        </td>
        <th class="text-white text-left uppercase tracking-wide" style="background:#374151; padding:4px 8px; font-size:10px;">Eyes</th>
        <td class="border-b border-slate-200" style="padding:2px 4px;">
          <input :value="modelValue.eyes" placeholder="—" class="w-full bg-transparent border-b border-transparent hover:border-slate-400 focus:border-orange-500 focus:outline-none" style="font-size:11px;font-family:inherit;" @input="set('eyes', ($event.target as HTMLInputElement).value)" />
        </td>
      </tr>
      <tr style="background:#f9fafb;">
        <th class="text-white text-left uppercase tracking-wide" style="background:#374151; padding:4px 8px; font-size:10px; white-space:nowrap;">Clothing</th>
        <td colspan="3" style="padding:2px 4px;">
          <textarea
            :value="modelValue.clothing"
            placeholder="—"
            rows="2"
            class="w-full bg-transparent border border-transparent hover:border-slate-300 focus:border-orange-500 focus:outline-none resize-none"
            style="font-size:11px;font-family:inherit;line-height:1.4;"
            @input="set('clothing', ($event.target as HTMLTextAreaElement).value)"
          />
        </td>
      </tr>
      </tbody>
    </table>

    <!-- Situation narrative -->
    <div class="mx-3 mb-3">
      <div class="text-white font-bold uppercase tracking-wide mb-1" style="background:#374151; padding:4px 8px; font-size:10px; letter-spacing:0.5px;">
        Situation / Last Known Activity
      </div>
      <textarea
        :value="modelValue.situation"
        placeholder="Describe the circumstances of disappearance…"
        rows="4"
        class="w-full border border-slate-200 hover:border-slate-400 focus:border-orange-500 focus:outline-none resize-none bg-transparent"
        style="padding:8px; font-size:11px; font-family:inherit; line-height:1.55;"
        @input="set('situation', ($event.target as HTMLTextAreaElement).value)"
      />
    </div>

    <!-- Contact block -->
    <div class="text-white text-center mx-3 mb-3" style="background:#1c1f26; padding:12px 16px;">
      <div class="font-semibold" style="font-size:13px;">
        If you have seen this missing person, please contact:
      </div>
      <div v-if="agencyName" class="text-slate-300 mt-0.5" style="font-size:11px;">{{ agencyName }}</div>
      <input
        :value="modelValue.contact_phone"
        placeholder="(555) 555-5555"
        class="text-center font-bold bg-transparent border-b border-transparent hover:border-white/50 focus:border-white focus:outline-none placeholder-slate-500 mt-1 block mx-auto"
        style="font-size:20px; letter-spacing:1.5px; color:#ea580c; font-family:inherit; width:260px;"
        @input="(e) => { const t = e.target as HTMLInputElement; const f = formatPhone(t.value); t.value = f; set('contact_phone', f) }"
      />
    </div>

  </div>
</template>
